import { useEffect, useState } from "react";
import { currentTokens, fileUrl } from "@shared/api";
import { Spinner } from "@shared/ui/spinner";

/**
 * Картинка из хранилища.
 *
 * Простой `<img src>` тут не годится: файл отдаётся только по токену, а
 * заголовков у `<img>` нет. Поэтому файл забирается запросом и подставляется
 * ссылкой на объект.
 *
 * Ссылки держатся в общей карте и не отзываются: один и тот же аватар
 * встречается в ленте десятки раз, и отзыв при размонтировании ломал бы
 * соседние копии. Держится ровно один объект на файл.
 *
 * Знак и стикер бывают и роликом WebM, как в Telegram. Что пришло, говорит тип
 * ответа сервера: он определён по байтам файла.
 */
type Loaded = { url: string; video: boolean };

const cache = new Map<string, Promise<Loaded>>();

function load(id: string): Promise<Loaded> {
  const known = cache.get(id);
  if (known) return known;

  const pending = (async () => {
    const token = currentTokens().access;
    const response = await fetch(fileUrl(id), {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) throw new Error(`file ${id}: ${response.status}`);

    const blob = await response.blob();
    return { url: URL.createObjectURL(blob), video: blob.type.startsWith("video/") };
  })();

  // Неудачу не запоминаем: следующий показ попробует снова.
  pending.catch(() => cache.delete(id));
  cache.set(id, pending);

  return pending;
}

/** Ссылка на объект картинки — та же, что у `FileImage`: просмотрщик берёт уже скачанное. */
export const fileObjectUrl = (id: string): Promise<string> => load(id).then((file) => file.url);

/**
 * Картинку — в буфер обмена, как «Скопировать изображение» в Discord. Буфер
 * Chromium принимает только PNG, поэтому прочее перерисовывается в него.
 */
export async function copyImage(id: string): Promise<void> {
  const blob = await (await fetch((await load(id)).url)).blob();
  let png = blob;
  if (blob.type !== "image/png") {
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
    bitmap.close();
    png = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((made) => (made ? resolve(made) : reject(new Error("png"))), "image/png"),
    );
  }
  await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
}

/**
 * Видео и звук — ссылкой на объект, без общей карты: файл тяжёлый (до 25 МБ),
 * держать его после закрытия незачем. Отзывает ссылку тот, кто её взял.
 */
export async function mediaObjectUrl(id: string): Promise<string> {
  const token = currentTokens().access;
  const response = await fetch(fileUrl(id), {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error(`file ${id}: ${response.status}`);
  return URL.createObjectURL(await response.blob());
}

/**
 * Тот же файл, но текстом.
 *
 * Своя карта, а не общая с картинками: там лежат ссылки на объекты, здесь —
 * само содержимое, и путать их значило бы однажды подставить одно вместо
 * другого.
 *
 * Отзыва тут нет по той же причине, что и там: один файл встречается в ленте
 * не раз, и чистка при размонтировании ломала бы соседние копии.
 */
const texts = new Map<string, Promise<string>>();

export function fileText(id: string): Promise<string> {
  const known = texts.get(id);
  if (known) return known;

  const pending = (async () => {
    const token = currentTokens().access;
    const response = await fetch(fileUrl(id), {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) throw new Error(`file ${id}: ${response.status}`);

    return await response.text();
  })();

  // Неудачу не запоминаем: следующий показ попробует снова.
  pending.catch(() => texts.delete(id));
  texts.set(id, pending);

  return pending;
}

export function FileImage({
  id,
  alt,
  className,
  width,
  height,
}: {
  id: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
}) {
  const [src, setSrc] = useState<Loaded | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setSrc(null);
    setFailed(false);

    load(id).then(
      (file) => alive && setSrc(file),
      () => alive && setFailed(true),
    );

    return () => {
      alive = false;
    };
  }, [id]);

  // Место занято в любом случае: размеры известны заранее, и лента не
  // прыгает ни во время загрузки, ни когда картинка не пришла вовсе.
  if (!src) {
    return (
      <span
        className={className}
        style={{ width, height, display: "grid", placeItems: "center" }}
        aria-label={alt}
      >
        {failed ? <span className="tiny muted">не загрузилось</span> : <Spinner />}
      </span>
    );
  }

  // Ролик играет как GIF: сам, по кругу и без звука.
  if (src.video) {
    return (
      <video
        src={src.url}
        aria-label={alt}
        className={className}
        width={width}
        height={height}
        autoPlay
        loop
        muted
        playsInline
        disablePictureInPicture
      />
    );
  }

  return <img src={src.url} alt={alt} className={className} width={width} height={height} />;
}

/**
 * Скачивание файла.
 *
 * Ссылка с `download` тоже не годится: заголовка у неё нет, и сервер ответит
 * отказом. Забираем содержимое запросом и отдаём браузеру как объект.
 */
export async function downloadFile(id: string, filename: string) {
  const token = currentTokens().access;
  const response = await fetch(fileUrl(id), {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) return;

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();

  // Ссылка одноразовая — держать её незачем.
  URL.revokeObjectURL(url);
}
